import { Check, Clear, CloudDownload, InfoOutlined } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Breadcrumbs, CircularProgress, FormControl, IconButton, InputLabel, Link, MenuItem, Paper, Select, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Tooltip, Typography } from "@mui/material";
import { useAtom } from 'jotai';
import { ChangeEvent, FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { modalEvents, permissionsAtom } from '../../atoms/atom';
import AppBar from "../../components/app-bar/AppBar";
import EventsDialog from '../../components/comparation-events/EventsDialog';
import { useAlert } from '../../components/contextProviders/AlertProvider';
import { getDownload } from '../../components/form/info-form-modal/InfoFormModal';
import { ExecutionService } from "../../services/ExecutionService";
import { ComparationEvent } from '../../types/ComparationEvent';
import { ComparationEventsDTO } from '../../types/ComparationEventsDTO';
import { getComparationEventsFilters } from '../../types/ComparationFilters';
import { useUtils } from "../../utils/utilFunctions";
import '../comparations/Comparations.scss';
import { getLoading } from '../execution-events/ExecutionEvents';

const ComparationEvents: FC = () => {
	const { id } = useParams<{ id: string }>();
	const [comparations, setComparations] = useState<ComparationEventsDTO | null>(null);
	const [expandedCompany, setExpandedCompany] = useState<string | false>('');
	const [filter, setFilter] = useState('SOME');
	const [search, setSearch] = useState('');
	const [tab, setTab] = useState<string | undefined>();
	const [loading, setLoading] = useState(true);
	const [loadingResult, setLoadingResult] = useState(false);
	const [comparationId, setComparationId] = useState<number>();
	const [eventType, setEventType] = useState<string>();
	const [permissions] = useAtom(permissionsAtom);
	const [, setOpen] = useAtom(modalEvents);
	const executionService = new ExecutionService();
	const { verifyPermission } = useUtils();
	const navigate = useNavigate();
	const { showError } = useAlert();

	const selected = comparations?.executions.find(execution => execution.executionDate === tab);

	const showDowloadButton = !!comparations?.executions
		.find(execution => execution.executionDate === tab)?.executionCompanies.length;

	const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
		setExpandedCompany(isExpanded ? panel : false);
	};

	const back = () => {
		navigate(-1);
	}

	const handleChangeSearch = (event: ChangeEvent<HTMLInputElement>) => {
		setSearch(event.target.value);
	}

	const hasErrorEvents = (v: ComparationEvent) => {
		return v.error2210 || v.error2220 || v.error2240;
	}

	const viewEvents = (id: number, event: string) => {
		setComparationId(id);
		setEventType(event);
		setOpen(true);
	}

	const handleDownload = async (id: number, name: string) => {
		setLoadingResult(true);
		if (!id) return;
		try {
			const response = await executionService.downloadResult(id);
			getDownload(response, "Relatório de eventos do eSocial - " + name.split("-")[0]);
		} catch (err) {
			showError("Erro ao baixar arquivo");
		} finally {
			setLoadingResult(false);
		}
	};

	useEffect(() => {
		const loadComparations = async () => {
			const result = await executionService.getExecutionEventsById(id);
			if (result) {
				setComparations(result.data);
				setTab(result.data?.executions[result.data.executions.length - 1].executionDate);
			}
			setLoading(false);
		};
		loadComparations();
	}, [id]);

	useEffect(() => {
		if (!!permissions.length) {
			verifyPermission(permissions, 'EVENTS');
		}
	}, []);

	return(
		<div className="comparations">
			<AppBar
				modulo="Eventos"
			/>
			<div className="component">
				<div className="top">
					<div className="etapas">
						<Breadcrumbs aria-label="breadcrumb" sx={{display: "flex", alignItems: "flex-end"}}>
							<Link variant="h6" sx={{cursor: "pointer"}} underline="hover" color="inherit" onClick={() => back()}>
								Execuções de eventos
							</Link>
							<Link
								variant="subtitle1"
								underline="hover"
								color="text.primary"
								aria-current="page"
								sx={{display: "flex", alignItems: "flex-end !important"}}
							>
								{comparations?.companyName}
							</Link>
						</Breadcrumbs>
					</div>
					<div className="search">
						<TextField
							sx={{width: '250px'}}
							label="Buscar funcionário pelo nome"
							id="employee-search"
							size="small"
							value={search}
							onChange={handleChangeSearch}
						/>
						<FormControl
							sx={{width: "250px"}}
							size='small'
						>
							<InputLabel>
								Filtrar funcionários
							</InputLabel>
							<Select
								label='Filtrar funcionários'
								id="select-categoria"
								value={filter}
								onChange={(e) => setFilter(e.target.value)}
							>
								{getComparationEventsFilters().map(item => (
									<MenuItem
										key={item.id}
										value={item.id}
									>
										{item.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</div>
				</div>
				<div className="tabela">
					<Tabs
						value={tab}
						onChange={(_, newTab) => setTab(newTab)}
						textColor="secondary"
					>
						{(comparations?.executions || []).map(filial => (
							<Tab label={filial.executionDate} value={filial.executionDate} />
						))}
					</Tabs>
					<Tooltip
						title="Baixar relatório"
						placement="right"
						sx={{display: showDowloadButton ? "" : "none"}}
					>
						<IconButton
							color="success"
							onClick={() => {
								handleDownload(selected?.id || 0, comparations?.companyName || "");
							}}
							className="execution-events-acoes"
							disabled={loadingResult}
						>
							{loadingResult ? <CircularProgress color="primary" size={'20px'}/> : <CloudDownload/>}
						</IconButton>
					</Tooltip>
					<Typography
						sx={{display: selected?.totalEsocial ? "" : "none"}}
						color="#e5e5e5" variant='h6'
					>
						Total de trabalhadores no eSocial: {selected?.totalEsocial}
					</Typography>
					<div className="listagem">
						{!comparations?.executions.some(execution => execution.executionCompanies.some(company =>
							filterComparations(company.comparations).length)) && !loading
							? <div className="sem-execucoes">Nenhum registro encontrado para o filtro aplicado...</div>
							: loading
								? getLoading()
								: <></>
						}
						{selected?.executionCompanies.map(company => (
							filterComparations(company.comparations).length
								? <Accordion
									expanded={expandedCompany === company.companyName}
									onChange={handleChange(company.companyName)}
									slotProps={{ transition: { unmountOnExit: true } }}
								>
									<AccordionSummary
										expandIcon={<ExpandMoreIcon />}
										aria-controls={company.companyName}
										id={company.companyName}
										sx={{display: "flex", justifyContent: "space-between"}}
									>
										<Typography variant='h6'>{company.companyName}</Typography>
										<Typography
											color={filterComparations(company.comparations).filter(hasErrorEvents).length ? 'error' : 'springgreen'}
											variant='h6'
											sx={{marginRight: "25vh"}}
										>
											{
												filterComparations(company.comparations).filter(hasErrorEvents).length
												? filterComparations(company.comparations).filter(hasErrorEvents).length + ' com divergência'
												: 'Nenhuma divergência'
											}
										</Typography>
									</AccordionSummary>
									<AccordionDetails>
										<TableContainer sx={{ maxHeight: 700, boxShadow: "0px 0px 1px 1px #b6b6b6" }} component={Paper}>
											<Table stickyHeader sx={{ minWidth: 650 }} aria-label="simple table">
												<TableHead>
												<TableRow>
													<TableCell>Funcionário</TableCell>
													<TableCell className='th-styled'>CPF</TableCell>
													<TableCell className='th-styled'>Eventos 2210</TableCell>
													<TableCell className='th-styled'>Eventos 2220</TableCell>
													<TableCell className='th-styled'>Eventos 2240</TableCell>
													<TableCell className='th-styled'>Não está no eSocial</TableCell>
													<TableCell className='th-styled'>Admissão preliminar</TableCell>
												</TableRow>
												</TableHead>
												<TableBody>
												{filterComparations(company.comparations).map((comparation) => (
													<TableRow
														key={comparation.name}
														sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
														className='row-line'
													>
														<TableCell component="th" scope="row">{verifyUnlinked(comparation)}</TableCell>
														<TableCell component="th" scope="row" sx={{width: "140px"}}>{comparation.cpf}</TableCell>
														{comparation.ignored
														? <TableCell colSpan={5} className='ignored'>
																<Typography sx={{textAlign: "center"}}>Funcionário ignorado. Data de desligamento inferior a 10/01/2022</Typography>
															</TableCell>
														: <>
															<TableCell className='th-styled'>{icon2210(comparation)}</TableCell>
															<TableCell className='th-styled'>{icon2220(comparation)}</TableCell>
															<TableCell className='th-styled'>{icon2240(comparation)}</TableCell>
															<TableCell className='th-styled'>{isFound(comparation.notFound)}</TableCell>
															<TableCell className='th-styled'>{isPreliminar(comparation)}</TableCell>
														</>
														}
													</TableRow>
												))}
												</TableBody>
											</Table>
										</TableContainer>
									</AccordionDetails>
								</Accordion>
								: <></>
							))
						}
					</div>
				</div>
			</div>
			{comparationId && eventType ? <EventsDialog id={comparationId} eventType={eventType}/> : <></>}
		</div>
	);

	function icon2210(comparation: ComparationEvent) {
		if (comparation.notFound || comparation.preliminarAdmission) {
			return <></>;
		}
		if (!comparation.has2210) {
			return <Typography>Nenhum</Typography>;
		}
		return <IconButton onClick={() => viewEvents(comparation.id, "2210")}>
			{comparation.error2210
				? <InfoOutlined sx={{fontSize: '4vh'}} color='error'/>
				: <Check sx={{fontSize: '4vh'}} color='success'/>
			}
		</IconButton>;
	}

	function icon2220(comparation: ComparationEvent) {
		if (comparation.notFound || comparation.preliminarAdmission) {
			return <></>;
		}
		if (!comparation.has2220) {
			return getNoneString()
		}
		return <IconButton onClick={() => viewEvents(comparation.id, "2220")}>
			{comparation.error2220
				? <InfoOutlined sx={{fontSize: '4vh'}} color='error'/>
				: <Check sx={{fontSize: '4vh'}} color='success'/>
			}
		</IconButton>;
	}

	function icon2240(comparation: ComparationEvent) {
		if (comparation.notFound || comparation.preliminarAdmission) {
			return <></>;
		}
		if (!comparation.has2240) {
			return getNoneString()
		}
		return <IconButton onClick={() => viewEvents(comparation.id, "2240")}>
			{comparation.error2240
				? <InfoOutlined sx={{fontSize: '4vh'}} color='error'/>
				: <Check sx={{fontSize: '4vh'}} color='success'/>
			}
		</IconButton>;
	}

	function isFound(value: boolean) {
		return value ? <Clear sx={{fontSize: '4vh'}} color='error'/> : <></>;
	}

	function isPreliminar(comparation: ComparationEvent) {
		return comparation.preliminarAdmission
			? <Clear sx={{fontSize: '4vh'}} color='error'/> : <></>
	}

	function verifyUnlinked(v: ComparationEvent) {
		return <Typography sx={{display: "flex", gap: "10px"}}>
			{v.name}{v.unlinked ? <Typography color="#dadd3eff">(sem vínculo)</Typography> : ""}
		</Typography>;
	}

	function filterComparations(comparations: ComparationEvent[]) {
		let filtered = comparations;
		switch (filter) {
			case "SOME":
				filtered = comparations.filter(hasErrorEvents);
				break;
			case "2210":
				filtered = comparations.filter(item => item.error2210);
				break;
			case "2220":
				filtered = comparations.filter(item => item.error2220);
				break;
			case "2240":
				filtered = comparations.filter(item => item.error2240);
				break;
			case "ESOCIAL":
				filtered = comparations.filter(item => item.notFound);
				break;
			case "PRELIMINAR":
				filtered = comparations.filter(item => item.preliminarAdmission);
				break;
			default:
				filtered = comparations;
		}
		if (search && search.trim() !== "") {
			const term = search.toLowerCase();
			filtered = filtered.filter(item => item.name.toLowerCase().includes(term));
		}
		return filtered;
	}

	function getNoneString() {
		return <Typography color="#f46156">Nenhum</Typography>
	}
}

export default ComparationEvents;